import {
  calculateMapFetching,
  clearMapFetching,
  selectMapFetchingResultByProjectId,
  selectMapFetchingState,
  useAppDispatch,
  useAppSelector,
} from '@core/core/store'
import type { ActiveEntity, BaseEntity } from '@core/core/types'
import { isStringNonEmpty } from '@core/core/utils'
import { usePrevious } from '@library/hooks'
import type { Nullable, ProjectId } from 'common/types'
import { useState } from 'react'

const generateName = (marker: Nullable<BaseEntity<'marker'>>, map: Nullable<BaseEntity<'map'>>) => {
  if (marker?.entityName != null && map?.entityName != null) {
    return `${marker.entityName}_${map.entityName}_Z`
  }
  return null
}

export const useMapFetching = (projectId: ProjectId) => {
  const dispatch = useAppDispatch()

  const mapFetchingResult = useAppSelector(state =>
    selectMapFetchingResultByProjectId(state, projectId),
  )

  const mapFetchingState = useAppSelector(selectMapFetchingState)

  const statistics =
    mapFetchingResult !== undefined
      ? { before: mapFetchingResult.before, after: mapFetchingResult.after }
      : undefined

  const [chosenMap, setChosenMap] = useState<Nullable<ActiveEntity<'map'>>>(null)
  const [chosenMarker, setChosenMarker] = useState<Nullable<ActiveEntity<'marker'>>>(null)
  const [newMapName, setNewMapName] = useState<Nullable<string>>(null)
  const previousMap = usePrevious(chosenMap)
  const previousMarker = usePrevious(chosenMarker)
  const previousName = usePrevious(newMapName)

  const isNameMarkerAndMapNotChanged =
    previousMap?.entityId === chosenMap?.entityId &&
    previousMarker?.entityName === chosenMarker?.entityName &&
    previousName === newMapName

  const isMapAndMarkerNotChosen = chosenMap === null || chosenMarker === null

  const isButtonDisabled = isMapAndMarkerNotChosen || isNameMarkerAndMapNotChanged

  const isCalculating = mapFetchingState.status === 'calculating'

  const onMapChange = (value: Nullable<ActiveEntity<'map'>>) => {
    setChosenMap(value)
    onNameChange(generateName(chosenMarker, value))
  }

  const onMarkerChange = (value: Nullable<ActiveEntity<'marker'>>) => {
    setChosenMarker(value)
    onNameChange(generateName(value, chosenMap))
  }

  const onNameChange = (value: Nullable<string>) => {
    setNewMapName(value)
  }

  const clearModalData = () => {
    setChosenMap(null)
    setChosenMarker(null)
    setNewMapName(null)
    dispatch(clearMapFetching())
  }

  const onCalculate = () => {
    const generatedName = generateName(chosenMarker, chosenMap)

    if (!isStringNonEmpty(newMapName)) {
      onNameChange(generatedName)
    }

    if (chosenMap?.entityId != null && chosenMarker != null && generatedName != null) {
      dispatch(
        calculateMapFetching({
          mapId: chosenMap.entityId,
          markerName: chosenMarker.entityName,
          newMapName: isStringNonEmpty(newMapName) ? newMapName : generatedName,
          projectId,
        }),
      )
    }
  }

  return {
    chosenMap,
    chosenMarker,
    newMapName,
    statistics,
    isButtonDisabled,
    isCalculating,
    clearModalData,
    onMapChange,
    onMarkerChange,
    onNameChange,
    onCalculate,
  }
}
